<template>
  <div ref="chartRef" class="ds-report-chart" :style="{ height: height }"></div>
</template>

<script>
import * as echarts from 'echarts'
import { getDsChartPalette, getDsSemanticColor } from '@/utils/dsChartTheme'

/**
 * DsReportChart —— ECharts 统一封装
 *
 * 解决的既有问题（见 UI-REFACTOR-SPEC.md §6.1 / §6.2）：
 *  E1 无 dispose 导致内存泄漏
 *  E1 匿名 resize 监听无法解绑
 *  E3 结构变化时需 notMerge，否则残留旧系列
 *  E4 legend.data 误用 -> 由本组件统一管理
 *  E6 无障碍 aria
 *
 * Props:
 *  - option  {Object} ECharts 配置
 *  - height {String}  高度
 *  - theme  {String}  预留主题名
 *  - loading{Boolean} 加载态（显示空白遮罩）
 *  - notMerge {Boolean} 结构变化时是否整体替换，默认 true
 *
 * Events:
 *  - chart-click   图表点击（用于下钻联动）
 *  - chart-ready   初始化完成
 */
export default {
  name: 'DsReportChart',
  props: {
    option: {
      type: Object,
      default: null
    },
    height: {
      type: String,
      default: '320px'
    },
    theme: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    notMerge: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      chart: null
    }
  },
  watch: {
    option: {
      deep: true,
      handler (val) {
        this.render(val)
      }
    },
    loading (val) {
      if (!this.chart) return
      if (val) {
        this.chart.showLoading({
          text: '加载中',
          color: getDsSemanticColor('primary'),
          textColor: getDsSemanticColor('gray'),
          maskColor: 'rgba(255,255,255,0.7)'
        })
      } else {
        this.chart.hideLoading()
      }
    }
  },
  mounted () {
    this.initChart()
    this.render(this.option)
    if (this.loading) {
      this.chart.showLoading({ text: '加载中', color: getDsSemanticColor('primary') })
    }
  },
  beforeDestroy () {
    this.destroyChart()
  },
  methods: {
    initChart () {
      if (!this.$refs.chartRef) return
      this.chart = echarts.init(this.$refs.chartRef, this.theme || undefined)
      this.chart.on('click', params => {
        this.$emit('chart-click', params)
      })
      // 容器尺寸变化时重绘（不依赖 window，滚动容器内同样生效）
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.chart) this.chart.resize()
        })
        this.resizeObserver.observe(this.$refs.chartRef)
      } else {
        this.handleResize = () => {
          if (this.chart) this.chart.resize()
        }
        window.addEventListener('resize', this.handleResize)
      }
    },
    destroyChart () {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
      if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize)
        this.handleResize = null
      }
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },
    render (option) {
      if (!this.chart || !option) return
      const merged = this.decorate(option)
      this.chart.setOption(merged, this.notMerge)
      this.$emit('chart-ready', this.chart)
    },
    /**
     * 统一注入色板、网格、无障碍等约定，避免每个页面重复配置
     */
    decorate (option) {
      const palette = getDsChartPalette()
      const result = Object.assign({}, option)
      if (!result.color) {
        result.color = palette
      }
      if (!result.aria) {
        result.aria = { enabled: true }
      }
      if (!result.grid) {
        result.grid = { left: 8, right: 16, top: 32, bottom: 8, containLabel: true }
      }
      if (!result.legend) {
        result.legend = { top: 0, icon: 'roundRect', itemWidth: 10, itemHeight: 8 }
      }
      if (!result.tooltip) {
        result.tooltip = { trigger: 'axis' }
      }
      // 坐标轴统一样式
      const axisStyle = {
        axisLine: { lineStyle: { color: '#D6DEE8' } },
        axisTick: { show: false },
        axisLabel: { color: '#6B7B92', fontSize: 12 },
        splitLine: { lineStyle: { color: '#E8EDF3' } }
      }
      if (result.xAxis && !result.xAxis.axisLine) {
        result.xAxis = Object.assign({}, result.xAxis, axisStyle, { splitLine: { show: false } })
      }
      if (result.yAxis && !result.yAxis.axisLine) {
        result.yAxis = Object.assign({}, result.yAxis, axisStyle)
      }
      // 系列补稳定 id 与名称（legend.data 由系列名推导，杜绝手写错位）
      if (Array.isArray(result.series)) {
        result.series = result.series.map((s, index) => {
          const series = Object.assign({}, s)
          if (!series.id) {
            series.id = 'series-' + index
          }
          if (!series.name && result.dataset && result.dataset.source) {
            // dataset 模式：系列名取 dataset 维度名
            const dim = result.series[index].encode && result.series[index].encode.y
            if (dim) series.name = dim
          }
          return series
        })
      }
      return result
    },
    /**
     * 暴露图表实例，供父组件做联动（echarts.connect 等）
     */
    getChart () {
      return this.chart
    },
    /**
     * 手动触发重绘（列设置/密度切换后布局变化时使用）
     */
    resize () {
      if (this.chart) this.chart.resize()
    }
  }
}
</script>

<style lang="scss" scoped>
.ds-report-chart {
  width: 100%;
}
</style>
